import { SUITE_MODULE_REGISTRY, type SuiteModuleEntry } from "../modules/registry";
import { getPepMarketingOverlay } from "./pep-catalog.service";

export type ProductCategory =
  | "people"
  | "sector"
  | "operations"
  | "commerce"
  | "platform";

export interface ProductMarketingProfile {
  id: string;
  tagline: string;
  benefits: string[];
  icon: string;
  accent: string;
  category: ProductCategory;
  trialAvailable: boolean;
  featured?: boolean;
}

const CATEGORY_LABELS: Record<ProductCategory, string> = {
  people: "RH & équipes",
  sector: "Secteurs métiers",
  operations: "Opérations & stock",
  commerce: "Commerce & finance",
  platform: "Plateforme",
};

/** Métadonnées marketing — complète le registre technique Suite. */
export const PRODUCT_MARKETING: Record<string, ProductMarketingProfile> = {
  rh: {
    id: "rh",
    tagline: "Paie, congés, recrutement — tout votre capital humain.",
    benefits: ["Bulletins & paie", "Congés & présences", "Recrutement"],
    icon: "users",
    accent: "#0d6efd",
    category: "people",
    trialAvailable: true,
    featured: true,
  },
  edu: {
    id: "edu",
    tagline: "Élèves, notes, bulletins — l'école numérique clé en main.",
    benefits: ["Bulletins & évaluations", "Absences & vie scolaire", "Stock cantine"],
    icon: "school",
    accent: "#8b5cf6",
    category: "sector",
    trialAvailable: true,
    featured: true,
  },
  hospitol: {
    id: "hospitol",
    tagline: "Patients, pharmacie, labo — l'hôpital modulaire.",
    benefits: ["Dossiers patients", "Pharmacie & stock", "Labo & imagerie"],
    icon: "building-hospital",
    accent: "#06b6d4",
    category: "sector",
    trialAvailable: true,
    featured: true,
  },
  inventory: {
    id: "inventory",
    tagline: "Stock, branches et réquisitions Kanakku.",
    benefits: ["Succursales", "Transferts stock", "Réquisitions"],
    icon: "building-warehouse",
    accent: "#f97316",
    category: "operations",
    trialAvailable: true,
    featured: true,
  },
  erp: {
    id: "erp",
    tagline: "Finance, achats et processus transverses.",
    benefits: ["Comptabilité", "Achats & fournisseurs", "Reporting"],
    icon: "building-factory-2",
    accent: "#64748b",
    category: "operations",
    trialAvailable: true,
  },
  mineportal: {
    id: "mineportal",
    tagline: "Portail minier — sites, ARSP, magasins terrain.",
    benefits: ["Sites d'exploitation", "Sous-traitance ARSP", "Planner & validations"],
    icon: "mountain",
    accent: "#ca8a04",
    category: "sector",
    trialAvailable: true,
  },
  subportal: {
    id: "subportal",
    tagline: "Conformité sous-traitants miniers.",
    benefits: ["Lots & exécution", "Fédération opérateurs", "Traçabilité ARSP"],
    icon: "users-group",
    accent: "#a16207",
    category: "sector",
    trialAvailable: true,
  },
  crm: {
    id: "crm",
    tagline: "Pipeline, contacts, devis et livraisons.",
    benefits: ["Contacts & pipeline", "Devis commerciaux", "Rapports ventes"],
    icon: "heart-handshake",
    accent: "#ec4899",
    category: "commerce",
    trialAvailable: true,
    featured: true,
  },
  billing: {
    id: "billing",
    tagline: "Factures, clients, paiements et portail client.",
    benefits: ["Factures & avoirs", "Encaissements", "Portail client"],
    icon: "receipt",
    accent: "#10b981",
    category: "commerce",
    trialAvailable: true,
    featured: true,
  },
  finance: {
    id: "finance",
    tagline: "Trésorerie et rapports comptables Kanakku.",
    benefits: ["Banque & caisse", "Virements", "Rapports GL"],
    icon: "chart-line",
    accent: "#14b8a6",
    category: "commerce",
    trialAvailable: true,
    featured: true,
  },
  procurement: {
    id: "procurement",
    tagline: "Achats fournisseurs et bons de commande.",
    benefits: ["Fournisseurs", "Commandes d'achat", "Notes de débit"],
    icon: "shopping-cart",
    accent: "#0f766e",
    category: "operations",
    trialAvailable: true,
    featured: true,
  },
  comex: {
    id: "comex",
    tagline: "Transit, fret et douane RDC.",
    benefits: ["Dossiers GUICE", "Corridors Katanga", "Finance dossier"],
    icon: "ship",
    accent: "#0369a1",
    category: "commerce",
    trialAvailable: true,
  },
  legal: {
    id: "legal",
    tagline: "Cabinet d'avocats — dossiers et honoraires.",
    benefits: ["Dossiers clients", "Échéances", "Temps passé"],
    icon: "scale",
    accent: "#4f46e5",
    category: "commerce",
    trialAvailable: true,
  },
  security: {
    id: "security",
    tagline: "Gardiennage, rondes et incidents terrain.",
    benefits: ["Rondes & QR", "Incidents", "Conformité"],
    icon: "shield-check",
    accent: "#dc2626",
    category: "people",
    trialAvailable: true,
  },
  product: {
    id: "product",
    tagline: "Catalogue articles PIM Kanakku.",
    benefits: ["Produits & SKU", "Catégories & unités", "Sync stock"],
    icon: "package",
    accent: "#7c3aed",
    category: "operations",
    trialAvailable: true,
    featured: true,
  },
  autoid: {
    id: "autoid",
    tagline: "Scan, codes-barres et lecteurs USB/HID.",
    benefits: ["Scan temps réel", "QR & barcodes", "Journal des scans"],
    icon: "scan",
    accent: "#0ea5e9",
    category: "platform",
    trialAvailable: true,
  },
  notifications: {
    id: "notifications",
    tagline: "Notifications in-app et préférences.",
    benefits: ["Boîte unifiée", "Préférences utilisateur", "Alertes métier"],
    icon: "bell",
    accent: "#6366f1",
    category: "platform",
    trialAvailable: false,
  },
};

export interface EnrichedProduct {
  entry: SuiteModuleEntry;
  marketing: ProductMarketingProfile;
}

function fallbackMarketing(entry: SuiteModuleEntry): ProductMarketingProfile {
  return {
    id: entry.id,
    tagline: entry.description,
    benefits: [],
    icon: "box",
    accent: "#64748b",
    category: "platform",
    trialAvailable: true,
  };
}

export function getMarketingProfile(
  entry: SuiteModuleEntry
): ProductMarketingProfile {
  const base = PRODUCT_MARKETING[entry.id] ?? fallbackMarketing(entry);
  const overlay = getPepMarketingOverlay(entry.id);
  if (!overlay) return base;
  return {
    ...base,
    ...overlay,
    benefits: overlay.benefits?.length ? overlay.benefits : base.benefits,
  };
}

export function enrichProduct(entry: SuiteModuleEntry): EnrichedProduct {
  const overlay = getPepMarketingOverlay(entry.id);
  return {
    entry: overlay?.displayName
      ? {
          ...entry,
          displayName: overlay.displayName,
          description: overlay.description ?? entry.description,
        }
      : entry,
    marketing: getMarketingProfile(entry),
  };
}

export function getCategoryLabel(category: ProductCategory): string {
  return CATEGORY_LABELS[category];
}

export function partitionProductsByLicense(licensedIds: string[]): {
  active: EnrichedProduct[];
  discover: EnrichedProduct[];
} {
  const licensed = new Set(licensedIds);
  const active: EnrichedProduct[] = [];
  const discover: EnrichedProduct[] = [];

  for (const entry of SUITE_MODULE_REGISTRY) {
    if (entry.id === "notifications") continue;
    const enriched = enrichProduct(entry);
    if (licensed.has(entry.id)) {
      active.push(enriched);
    } else if (enriched.marketing.trialAvailable) {
      discover.push(enriched);
    }
  }

  const byFeatured = (a: EnrichedProduct, b: EnrichedProduct) =>
    Number(b.marketing.featured) - Number(a.marketing.featured);

  active.sort(byFeatured);
  discover.sort(byFeatured);

  return { active, discover };
}

export function groupDiscoverByCategory(
  products: EnrichedProduct[]
): Map<ProductCategory, EnrichedProduct[]> {
  const map = new Map<ProductCategory, EnrichedProduct[]>();
  for (const product of products) {
    const cat = product.marketing.category;
    const list = map.get(cat) ?? [];
    list.push(product);
    map.set(cat, list);
  }
  return map;
}
